import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { generateInvoiceHTML, INVOICE_COPY_TYPE } from './InvoiceTemplates/InvoiceGenerator'
import { getSalesOrderHelper } from '../../helper/home-helper'
import Config from '../../config/config'

/**
 * Print Customer Copy of the invoice
 * @param {string} invoiceId - The sales order ID
 * @param {string} servedByName - Name of the user who served the customer
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 * @returns {Promise<boolean>} - Returns true if printing was successful
 */
export const handlePrintInvoice = async (invoiceId, servedByName = null, pharmacyName = null) => {
  console.log('handlePrintInvoice called with ID:', invoiceId)
  
  try {
    const invoiceData = await getSalesOrderHelper(invoiceId)
    console.log('Invoice data received:', invoiceData)

    if (!invoiceData) {
      console.error('Invoice data not found')
      return false
    }

    const branchData = invoiceData.branch_details || invoiceData.branch || {}
    console.log('Branch data:', branchData)

    // Add served by name to invoice data if provided
    if (servedByName) {
      invoiceData.served_by = servedByName
    }

    // Add pharmacy name to branch data if provided (from loginData)
    if (pharmacyName) {
      branchData.pharmacy_name = pharmacyName
    }

    // Transform logo URL to full URL
    if (branchData.logo) {
      branchData.logo = Config.getFullImageUrl(branchData.logo)
    }

    // Print customer copy only
    printCustomerCopy(invoiceData, branchData)

    return true
  } catch (error) {
    console.error('Error in handlePrintInvoice:', error)
    throw error
  }
}

/**
 * Print a single copy of the invoice
 * @param {string} invoiceId - The sales order ID
 * @param {string} copyType - PHARMACY_COPY or CUSTOMER_COPY
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 * @returns {Promise<boolean>}
 */
export const handlePrintSingleCopy = async (invoiceId, copyType, pharmacyName = null) => {
  const invoiceData = await getSalesOrderHelper(invoiceId)

  if (!invoiceData) {
    console.error('Invoice data not found')
    return false
  }

  const branchData = invoiceData.branch_details || invoiceData.branch || {}

  // Add pharmacy name to branch data if provided (from loginData)
  if (pharmacyName) {
    branchData.pharmacy_name = pharmacyName
  }

  // Transform logo URL to full URL
  if (branchData.logo) {
    branchData.logo = Config.getFullImageUrl(branchData.logo)
  }

  await printInvoiceCopy(invoiceData, branchData, copyType)

  return true
}

/**
 * Print Customer Copy only
 */
const printCustomerCopy = (invoiceData, branchData) => {
  const customerHTML = generateInvoiceHTML(invoiceData, branchData, INVOICE_COPY_TYPE.CUSTOMER_COPY)

  // Open print window
  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    alert('Please allow popups for this site to enable printing')
    return
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice - ${invoiceData.invoice_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Poppins', 'Arial', sans-serif;
            background: white;
          }
          .customer-copy-page {
            max-width: 300px;
            margin: 0 auto;
            padding: 10px;
          }
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .customer-copy-page {
              page-break-inside: avoid;
            }
          }
          @media screen {
            .customer-copy-page {
              border: 1px solid #ccc;
              margin: 20px auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="customer-copy-page">
          ${customerHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 1000);
            }, 500);
          };
        </script>
      </body>
    </html>
  `)

  printWindow.document.close()
}

/**
 * Print a single invoice copy
 */
const printInvoiceCopy = async (invoiceData, branchData, copyType) => {
  const invoiceHTML = generateInvoiceHTML(invoiceData, branchData, copyType)
  const copyTitle =
    copyType === INVOICE_COPY_TYPE.PHARMACY_COPY
      ? 'Pharmacy Copy'
      : 'Customer Copy'

  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${copyTitle} - ${invoiceData.invoice_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            font-family: 'Poppins', 'Arial', sans-serif;
            font-size: 15px;
            background: white;
            height: auto !important;
            overflow: visible !important;
            width: 86mm;
          }
          .invoice-container {
            width: 86mm;
            margin: 0;
            padding: 0;
            height: auto !important;
            overflow: visible !important;
          }
          h1, h2, h3 {
            font-size: 15px;
            line-height: 1.4;
          }
          p, span, td, th, div {
            font-size: 15px;
            line-height: 1.6;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th {
            font-size: 15px;
            font-weight: 600;
            padding: 4px 0;
          }
          td {
            font-size: 15px;
            padding: 4px 0;
          }
          @media print {
            @page {
              size: 86mm auto;
              margin: 0;
            }
            html, body {
              margin: 0;
              padding: 0;
              width: 86mm;
              height: auto !important;
              overflow: visible !important;
            }
            .invoice-container {
              width: 86mm;
              padding: 0;
              height: auto !important;
              overflow: visible !important;
            }
            .invoice-template {
              height: auto !important;
              overflow: visible !important;
            }
            table, thead, tbody, tr, th, td {
              page-break-inside: auto !important;
              break-inside: auto !important;
            }
            .invoice-template > *:not(table) {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          ${invoiceHTML}
        </div>
      </body>
    </html>
  `

  // Create hidden iframe
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = 'none'
  iframe.style.visibility = 'hidden'

  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentWindow.document
  iframeDoc.open()
  iframeDoc.write(printContent)
  iframeDoc.close()

  // Wait for fonts and content to load then print
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
      } catch (err) {
        console.error('Print failed:', err)
      } finally {
        // Remove iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe)
        }, 2000)
      }
    }, 800)
  }
}

/**
 * Print invoice directly from invoice data (no API call)
 * Useful when you already have the invoice data
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 */
export const printInvoiceFromData = (invoiceData, copyType = INVOICE_COPY_TYPE.CUSTOMER_COPY, pharmacyName = null) => {
  const branchData = invoiceData.branch_details || invoiceData.branch || {}
  
  // Add pharmacy name to branch data if provided (from loginData)
  if (pharmacyName) {
    branchData.pharmacy_name = pharmacyName
  }

  // Transform logo URL to full URL
  if (branchData.logo) {
    branchData.logo = Config.getFullImageUrl(branchData.logo)
  }
  
  printInvoiceCopy(invoiceData, branchData, copyType)
}

/**
 * Download an invoice copy as a PDF from invoice data (no API call).
 * Renders the same HTML used for printing into an offscreen container,
 * rasterises it with html2canvas and saves it via jsPDF.
 * Defaults to the Customer Copy.
 * @param {object} invoiceData
 * @param {string} copyType - PHARMACY_COPY or CUSTOMER_COPY
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 * @returns {Promise<boolean>}
 */
export const downloadInvoiceFromData = async (
  invoiceData,
  copyType = INVOICE_COPY_TYPE.CUSTOMER_COPY,
  pharmacyName = null
) => {
  const branchData = invoiceData.branch_details || invoiceData.branch || {}

  if (pharmacyName) {
    branchData.pharmacy_name = pharmacyName
  }
  if (branchData.logo) {
    branchData.logo = Config.getFullImageUrl(branchData.logo)
  }

  const invoiceHTML = generateInvoiceHTML(invoiceData, branchData, copyType)

  // Offscreen container matching the 80mm customer-copy width.
  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-9999px'
  container.style.top = '0'
  container.style.width = '300px'
  container.style.padding = '10px'
  container.style.background = '#FFFFFF'
  container.style.fontFamily = "'Poppins', 'Arial', sans-serif"
  container.innerHTML = invoiceHTML
  document.body.appendChild(container)

  try {
    // Give fonts/logo a moment to load before rasterising.
    await new Promise((r) => setTimeout(r, 300))

    const canvas = await html2canvas(container, {
      useCORS: true,
      allowTaint: true,
      scale: 2,
      backgroundColor: '#FFFFFF',
      windowWidth: container.scrollWidth,
      windowHeight: container.scrollHeight,
    })

    const imgData = canvas.toDataURL('image/png')
    // Page width fixed at 80mm; height scales to the content.
    const pdfWidth = 80
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width
    const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight])
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
    pdf.save(`Invoice-${invoiceData.invoice_number || 'customer-copy'}.pdf`)
    return true
  } catch (error) {
    console.error('Error in downloadInvoiceFromData:', error)
    throw error
  } finally {
    document.body.removeChild(container)
  }
}

export { INVOICE_COPY_TYPE }
